import { AlertDialog, useDialog as useDaxDialog } from '@global-dax-ad-platform/dax-components';
import { FileObject, FormikFileUpload } from '@global-engineering-shared/gweb-material-formik';
import { Box, Typography } from '@global-engineering-shared/gweb-material-ui';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import LoadableComponent from 'components/LoadableComponent/LoadableComponent';
import { useRefresh } from 'context/RefreshProvider/RefreshProvider';
import { useStyles } from 'features/DataManagement/components/FileUploadDialog/FileUploadDialog.styles';
import useFetchData from 'hooks/useFetchData';
import { DataManagmentUploads } from 'utils/dataManagmentUploads';
import {
  DATA_MANAGEMENT_DATA_API_URL,
  DRAFTS_PAGE_URL,
  FILE_UPLOAD_DIALOG_ID,
  FILE_UPLOAD_RESULT_DIALOG_ID,
} from 'utils/defaults';
import { formatFileType } from 'utils/formatColumns';

interface IFileUploadDialogProps {
  dataType: DataManagmentUploads;
}

interface IFileUploadFormValues {
  file: FileObject[] | null;
}

const FileUploadDialog: React.FC<IFileUploadDialogProps> = ({ dataType }) => {
  const { openDialog, closeDialog } = useDaxDialog();
  const classes = useStyles();

  const dialogId = FILE_UPLOAD_DIALOG_ID + dataType;
  const resultDialogId = FILE_UPLOAD_RESULT_DIALOG_ID + dataType;
  const { uploadFile } = useFetchData();
  const { triggerRefresh } = useRefresh();
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const isOverridesUpload = dataType === DataManagmentUploads.OVERRIDES;
  const dismissDialog = () => {};

  const onUploadSubmit = async (
    file: FileObject[] | null,
    resetForm: FormikHelpers<IFileUploadFormValues>['resetForm']
  ) => {
    const endpoint = `${DATA_MANAGEMENT_DATA_API_URL}/${dataType.toUpperCase()}/upload`;
    const fileToUpload = file?.[0]?.file;

    if (!fileToUpload) return;

    setIsUploading(true);
    const uploadedSuccessfully = await uploadFile(endpoint, fileToUpload);
    setIsUploading(false);
    if (uploadedSuccessfully) {
      triggerRefresh();
      resetForm();
      closeDialog(dialogId);
      openDialog(resultDialogId);
    }
  };

  return (
    <>
      <AlertDialog
        dialogId={resultDialogId}
        hideCancelButton={!isOverridesUpload}
        cancelLabel="Continue"
        content={{
          title: `${formatFileType(dataType)} File Upload Successful`,
          main: (
            <Typography color="textPrimary" variant="body2">
              {formatFileType(dataType)} file upload successful.
              {isOverridesUpload && ' You will be able to see new data on the drafts page shortly.'}
            </Typography>
          ),
        }}
        action={
          isOverridesUpload
            ? {
                label: 'See Drafts',
                onClick: () => navigate(DRAFTS_PAGE_URL),
              }
            : { label: 'Continue', onClick: dismissDialog }
        }
      />
      <Formik<IFileUploadFormValues>
        initialValues={{ file: null }}
        onSubmit={(values, { resetForm }) => {
          onUploadSubmit(values.file, resetForm);
        }}
      >
        {({ submitForm }) => (
          <AlertDialog
            dialogId={dialogId}
            hideCancelButton
            content={{
              title: `Upload CSV File for ${formatFileType(dataType)}`,
              main: (
                <Box className={classes.dialogMainContent}>
                  <Form id="fileUploadForm" data-testid="fileUploadForm" className={classes.form}>
                    <LoadableComponent loading={isUploading}>
                      <FormikFileUpload
                        name="file"
                        accept={{ 'application/csv': ['.csv'] }}
                        fullWidth
                      />
                    </LoadableComponent>
                  </Form>
                </Box>
              ),
            }}
            action={{
              label: 'Submit',
              onClick: submitForm,
              loading: isUploading,
              closeOnAccept: false,
            }}
          />
        )}
      </Formik>
    </>
  );
};

export default FileUploadDialog;
